import { lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet-async";

const ProgramsSection = lazy(() => import("@/components/ProgramsSection"));
const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const ResultsSection = lazy(() => import("@/components/ResultsSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));

const Index = () => {
  return (
    <>
      <Helmet>
        <title>Kleines Genie Österreich – Schule für mentale Arithmetik für Kinder</title>
        <meta
          name="description"
          content="Kleines Genie Österreich – Schule Nr. 1 nach Wahl der Eltern. Programme für Mentale Arithmetik, Numicon und mehr für Kinder von 3 bis 12 Jahren. Kostenlose Probestunde!"
        />
        <meta name="keywords" content="Kleines Genie, Wien, mentale Arithmetik, Numicon, Schule für Kinder, intellektuelle Entwicklung, Gehirnentwicklung, Abakus" />
        <meta property="og:title" content="Kleines Genie Österreich – Schule für mentale Arithmetik" />
        <meta property="og:description" content="Programme für optimale intellektuelle Entwicklung von Kindern von 3 bis 12 Jahren in Wien und Schwechat. Kostenlose Probestunde!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.kleines-genie.at/" />
        <meta property="og:locale" content="de_AT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kleines Genie Österreich – Schule für mentale Arithmetik" />
        <meta name="twitter:description" content="Programme für Mentale Arithmetik und Numicon für Kinder von 3 bis 12 Jahren in Wien und Schwechat. Kostenlose Probestunde!" />
        <link rel="canonical" href="https://www.kleines-genie.at/" />
        <link rel="alternate" hrefLang="de" href="https://www.kleines-genie.at/" />
        <link rel="alternate" hrefLang="x-default" href="https://www.kleines-genie.at/" />

        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Kleines Genie Österreich",
            "description": "Schule für optimale intellektuelle Entwicklung von Kindern von 3 bis 12 Jahren durch Programme der mentalen Arithmetik, Numicon und andere wissenschaftlich bewiesene Methoden.",
            "address": [
              {
                "@type": "PostalAddress",
                "streetAddress": "Großenzersdorfer Straße 56/1",
                "addressLocality": "Wien",
                "postalCode": "1220",
                "addressCountry": "AT"
              },
              {
                "@type": "PostalAddress",
                "streetAddress": "Ableidingergasse 4/24",
                "addressLocality": "Schwechat",
                "postalCode": "2320",
                "addressCountry": "AT"
              }
            ],
            "telephone": "+436509861983",
            "url": "https://www.kleines-genie.at",
            "areaServed": [
              { "@type": "City", "name": "Wien" },
              { "@type": "City", "name": "Schwechat" }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Programme für Kinder",
              "itemListElement": [
                {
                  "@type": "Course",
                  "name": "Numicon",
                  "description": "Oxford-Programm zur Entwicklung vormathematischer Fähigkeiten bei Kindern von 3 bis 6 Jahren.",
                  "provider": { "@type": "EducationalOrganization", "name": "Kleines Genie Österreich" },
                  "audience": { "@type": "EducationalAudience", "educationalRole": "student", "audienceType": "Kinder 3-6 Jahre" }
                },
                {
                  "@type": "Course",
                  "name": "Mentale Arithmetik",
                  "description": "Programm zur Entwicklung kognitiver Fähigkeiten bei Kindern von 5 bis 12 Jahren.",
                  "provider": { "@type": "EducationalOrganization", "name": "Kleines Genie Österreich" },
                  "audience": { "@type": "EducationalAudience", "educationalRole": "student", "audienceType": "Kinder 5-12 Jahre" }
                },
                {
                  "@type": "Course",
                  "name": "Kleiner Alleswisser",
                  "description": "Programm für das frühe Lebensalter zur Förderung von Neugierde, Kreativität sowie mathematischen und sprachlichen Fähigkeiten bei Kindern von 3,5 bis 6 Jahren.",
                  "provider": { "@type": "EducationalOrganization", "name": "Kleines Genie Österreich" },
                  "audience": { "@type": "EducationalAudience", "educationalRole": "student", "audienceType": "Kinder 3,5-6 Jahre" }
                },
                {
                  "@type": "Course",
                  "name": "Programmieren für Kinder",
                  "description": "Programmieren mit SCRATCH und Python, Robotik mit dem mBot2-Roboter und 3D-Modellierung für Kinder von 9 bis 12 Jahren.",
                  "provider": { "@type": "EducationalOrganization", "name": "Kleines Genie Österreich" },
                  "audience": { "@type": "EducationalAudience", "educationalRole": "student", "audienceType": "Kinder 9-12 Jahre" }
                }
              ]
            }
          }
        `}</script>

        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Für welche Altersgruppen sind die Programme bei Kleines Genie geeignet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Unsere Programme sind für Kinder von 3 bis 12 Jahren: Numicon (3-6 Jahre), Kleiner Alleswisser (3,5-6 Jahre), Mentale Arithmetik (5-12 Jahre) und Programmieren für Kinder (9-12 Jahre)."
                }
              },
              {
                "@type": "Question",
                "name": "Ist die Probestunde kostenlos?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, die erste Probestunde ist völlig kostenlos und dient zur Einschätzung der Fähigkeiten des Kindes und zur Empfehlung eines geeigneten Programms."
                }
              },
              {
                "@type": "Question",
                "name": "Welches Programm ist das beste für mein Kind?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Die Wahl des Programms hängt vom Alter und den Interessen des Kindes ab. Für Kinder von 3-6 Jahren empfehlen wir Numicon oder Kleiner Alleswisser, für ältere Kinder die Mentale Arithmetik oder Programmieren für Kinder."
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      <Navbar />
      <main>
        <HeroSection />
        <Suspense fallback={null}>
          <ProgramsSection />
          <WhyUsSection />
          <ResultsSection />
          <TestimonialsSection />
          <ContactSection />
        </Suspense>
      </main>
      <Footer />
    </>
  );
};

export default Index;
